import { Button } from '@upme/ui/components/button';
import { Input } from '@upme/ui/components/input';
import { Label } from '@upme/ui/components/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@upme/ui/components/select';
import { Switch } from '@upme/ui/components/switch';
import { type Resolver, useForm } from 'react-hook-form';
import type { z } from 'zod';
import { zodResolver } from '../../../app/zod-resolver';
import { type PaymentProviderDto, ProviderFormSchema, type ProviderFormValues } from '../schema';

interface Props {
  existing?: PaymentProviderDto;
  onSubmit: (values: ProviderFormValues) => void;
  pending?: boolean;
}

export function ProviderForm({ existing, onSubmit, pending }: Props) {
  const form = useForm<ProviderFormValues>({
    resolver: zodResolver(
      ProviderFormSchema as unknown as z.ZodType<ProviderFormValues>,
    ) as Resolver<ProviderFormValues>,
    defaultValues: {
      type: existing?.type ?? 'PAYPAL',
      name: existing?.name ?? 'PayPal',
      mode: existing?.mode ?? 'sandbox',
      isActive: existing?.isActive ?? true,
      clientId: '',
      clientSecret: '',
      webhookId: '',
      apiKey: '',
      webhookSecret: '',
      offerEnergy10: '',
      offerEnergy20: '',
      offerEnergy50: '',
      offerProPromo: '',
    },
  });

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 max-w-xl">
      <div>
        <Label htmlFor="name">Название</Label>
        <Input id="name" {...form.register('name')} placeholder="PayPal (основной)" />
      </div>

      <div>
        <Label htmlFor="type">Тип</Label>
        <Select
          value={form.watch('type')}
          onValueChange={(v) => form.setValue('type', v as 'PAYPAL' | 'LAVATOP')}
        >
          <SelectTrigger id="type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="PAYPAL">PayPal</SelectItem>
            <SelectItem value="LAVATOP">LavaTop</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="mode">Режим</Label>
        <Select
          value={form.watch('mode')}
          onValueChange={(v) => form.setValue('mode', v as 'sandbox' | 'live')}
        >
          <SelectTrigger id="mode">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="sandbox">sandbox</SelectItem>
            <SelectItem value="live">live</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {form.watch('type') === 'PAYPAL' && (
        <>
          <div>
            <Label htmlFor="clientId">
              Client ID{existing ? ` (текущий: ${existing.clientIdMasked ?? '—'})` : ''}
            </Label>
            <Input
              id="clientId"
              {...form.register('clientId')}
              placeholder={existing ? 'оставьте пустым — не менять' : ''}
            />
          </div>

          <div>
            <Label htmlFor="clientSecret">
              Client Secret{existing ? ` (${existing.clientSecretMasked ?? '—'})` : ''}
            </Label>
            <Input
              id="clientSecret"
              type="password"
              {...form.register('clientSecret')}
              placeholder={existing ? 'не менять' : ''}
            />
          </div>

          <div>
            <Label htmlFor="webhookId">
              Webhook ID{existing ? ` (${existing.webhookIdMasked || '—'})` : ' (необязательно)'}
            </Label>
            <Input
              id="webhookId"
              {...form.register('webhookId')}
              placeholder={existing ? 'не менять' : 'можно заполнить позже — после регистрации вебхука'}
            />
          </div>
        </>
      )}

      {form.watch('type') === 'LAVATOP' && (
        <>
          <div>
            <Label htmlFor="apiKey">API-ключ{existing ? ` (${existing.apiKeyMasked ?? '—'})` : ''}</Label>
            <Input
              id="apiKey"
              type="password"
              {...form.register('apiKey')}
              placeholder={existing ? 'не менять' : ''}
            />
          </div>
          <div>
            <Label htmlFor="webhookSecret">
              Webhook secret (X-Api-Key вебхука){existing ? ` (${existing.webhookSecretMasked ?? '—'})` : ''}
            </Label>
            <Input
              id="webhookSecret"
              type="password"
              {...form.register('webhookSecret')}
              placeholder={existing ? 'не менять' : 'настраивается в кабинете LavaTop'}
            />
          </div>
          <div>
            <Label htmlFor="offerEnergy10">Offer ID — пакет $10 (energy-10)</Label>
            <Input
              id="offerEnergy10"
              {...form.register('offerEnergy10')}
              placeholder={existing?.offers?.['energy-10'] ?? ''}
            />
          </div>
          <div>
            <Label htmlFor="offerEnergy20">Offer ID — пакет $20 (energy-20)</Label>
            <Input
              id="offerEnergy20"
              {...form.register('offerEnergy20')}
              placeholder={existing?.offers?.['energy-20'] ?? ''}
            />
          </div>
          <div>
            <Label htmlFor="offerEnergy50">Offer ID — пакет $50 (energy-50)</Label>
            <Input
              id="offerEnergy50"
              {...form.register('offerEnergy50')}
              placeholder={existing?.offers?.['energy-50'] ?? ''}
            />
          </div>
          <div>
            <Label htmlFor="offerProPromo">Offer ID — Pro + промо $250 (pro-promo)</Label>
            <Input
              id="offerProPromo"
              {...form.register('offerProPromo')}
              placeholder={existing?.offers?.['pro-promo'] ?? ''}
            />
          </div>
        </>
      )}

      <div className="flex items-center gap-2">
        <Switch
          id="isActive"
          checked={form.watch('isActive')}
          onCheckedChange={(v) => form.setValue('isActive', v)}
        />
        <Label htmlFor="isActive">Активен</Label>
      </div>

      <Button type="submit" disabled={pending}>
        {existing ? 'Сохранить' : 'Создать'}
      </Button>
    </form>
  );
}
